import { useMemo, useEffect, useState } from "react";
import "instantsearch.css/themes/satellite.css";
import "./SupplierInventorySearch.css";
import "./RefinementList.css";
import { InstantSearch, Configure, useInfiniteHits } from "react-instantsearch";
import { Text, Box, Flex, Badge, Button, Icon } from "@chakra-ui/react";
import { MdLocationOn } from "react-icons/md";
import { BsEyeFill } from "react-icons/bs";
import { FaUserCheck } from "react-icons/fa";
import { SupplierProgressBadge, SupplierText } from "./SupplierBadge";
import { OfflineSupplierEntry } from "./types";
import {
  mergeOfflinePartsIntoHits,
  computeFulfilledCount,
} from "./utils/offlinePartAvailabilityUtils";
import { applySupplierInventoryDeltasToParts } from "./utils/supplierInventoryDeltaUtils";
import { buildSupplierMetrics } from "./utils/supplierMetricsUtils";
import { RequestPartConfirmationButton } from "./RequestPartConfirmationButton";

interface GetPinnedComponentProps {
  handleSupplierClick: (groupKey: string, isFromTopSuppliers?: boolean) => void;
  onRequestPartConfirmation?: (groupKey: string) => void;
  pinnedSupplierIds: string[];
  setIsPinnedItem: (count: number) => void;
  preFacetedPartNumbers: any[];
  typesenseInstantsearchAdapter: any;
  onSelectionClose: () => void;
  customerSupplierAssociations: any[];
  customerGeoPoint: { latitude: number; longitude: number };
  handleSelectListItem: (item: any, supplierParts?: any[]) => void;
  handleAddToSelectedSupplierBucket: (item: any, supplierParts?: any[]) => void;
  setPinnedHitMap: (map: Map<string, any[]>) => void;
  supplierDiscoveryCollectionName: string;
  preFacetedPartNumbersMap: Map<string, number>;
  totalRequestedQuantity: number;
  pinnedHitMap: Map<string, any[]>;
  selectedSuppliers?: any[];
  supplierCityMap: Map<string, string>;
  isEstimateOrder: boolean;
  offlinePartAvailabilityDiscrepanciesPerSupplierMap?: Map<
    string,
    OfflineSupplierEntry
  >;
  supplierInventoryDeltaQuantityMap?: Map<string, number>;
}

const PinnedData = ({
  handleSupplierClick,
  onRequestPartConfirmation,
  setIsPinnedItem,
  customerSupplierAssociations,
  preFacetedPartNumbers,
  handleSelectListItem,
  handleAddToSelectedSupplierBucket,
  customerGeoPoint,
  onSelectionClose,
  setPinnedHitMap,
  typesenseInstantsearchAdapter,
  supplierDiscoveryCollectionName,
  pinnedSupplierIds,
  preFacetedPartNumbersMap,
  totalRequestedQuantity,
  pinnedHitMap,
  supplierCityMap,
  isEstimateOrder,
  offlinePartAvailabilityDiscrepanciesPerSupplierMap,
  supplierInventoryDeltaQuantityMap,
}: any) => {
  const { hits } = useInfiniteHits();
  const userLatitude = customerGeoPoint?.latitude;
  const userLongitude = customerGeoPoint?.longitude;
  const [pinnedMetrics, setPinnedMetrics] = useState<any[]>([]);

  useEffect(() => {
    const newPinnedHitMap = new Map<string, any[]>();

    hits.forEach((hit: any) => {
      const groupKey = hit._group_key[0];
      if (!groupKey) return;
      const [, supplierId] = groupKey.split("::");
      let parts = hit._grouped_hits;
      const offlineEntry =
        offlinePartAvailabilityDiscrepanciesPerSupplierMap?.get(groupKey);
      if (offlineEntry) {
        parts = mergeOfflinePartsIntoHits(
          parts,
          offlineEntry.parts,
          offlineEntry.updatedAt,
        );
      }
      parts = applySupplierInventoryDeltasToParts({
        supplierId,
        parts,
        supplierInventoryDeltaQuantityMap,
      });
      newPinnedHitMap.set(groupKey, parts);
    });

    if (
      offlinePartAvailabilityDiscrepanciesPerSupplierMap &&
      offlinePartAvailabilityDiscrepanciesPerSupplierMap.size > 0
    ) {
      Array.from(
        offlinePartAvailabilityDiscrepanciesPerSupplierMap.entries(),
      ).forEach((entryArr) => {
        const [groupKey, entry] = entryArr as [string, OfflineSupplierEntry];
        if (!pinnedSupplierIds.includes(entry.supplierId)) return;
        if (newPinnedHitMap.has(groupKey)) return;

        const updatedAt = entry.updatedAt
          ? Math.floor(new Date(entry.updatedAt).getTime())
          : undefined;
        const syntheticParts = entry.parts.map((p: any) => ({
          rootPartNumber: p.rootPartNumber,
          familyQuantity: p.familyQuantity,
          supplierInfo: entry.groupKey,
          ...(updatedAt != null && { updatedAt }),
        }));
        const deltaAdjustedParts = applySupplierInventoryDeltasToParts({
          supplierId: entry.supplierId,
          parts: syntheticParts,
          supplierInventoryDeltaQuantityMap,
        });
        newPinnedHitMap.set(groupKey, deltaAdjustedParts);
      });
    }

    setPinnedHitMap(newPinnedHitMap);

    const metricsMap = new Map<string, any>();
    const typesenseGroupKeys = new Set(
      hits.map((h: any) => h._group_key?.[0]).filter(Boolean),
    );
    newPinnedHitMap.forEach((parts, groupKey) => {
      const [supplierName, supplierId, coords] = groupKey.split("::");
      const isFromTypesense = typesenseGroupKeys.has(groupKey);
      const syntheticHit = { found: parts.length, hits: parts };
      const metrics = buildSupplierMetrics(
        syntheticHit,
        supplierName,
        supplierId,
        coords,
        userLatitude,
        userLongitude,
        customerSupplierAssociations,
        preFacetedPartNumbers,
        preFacetedPartNumbersMap,
        totalRequestedQuantity,
        {
          supplierCity: supplierCityMap.get(supplierId),
          fulfilledCountOverride: computeFulfilledCount(
            parts,
            preFacetedPartNumbersMap,
          ),
          ...(isFromTypesense ? {} : { distanceKmOverride: 0 }),
        },
      );
      if (metrics && (metrics.fulfilledCount > 0 || metrics.totalPartsQuantity > 0)) {
        metricsMap.set(groupKey, metrics);
      }
    });

    setPinnedMetrics(Array.from(metricsMap.entries()));
    setIsPinnedItem(metricsMap.size);
  }, [
    hits,
    customerGeoPoint,
    customerSupplierAssociations,
    preFacetedPartNumbers,
    setIsPinnedItem,
    pinnedSupplierIds,
    userLatitude,
    userLongitude,
    preFacetedPartNumbersMap,
    totalRequestedQuantity,
    offlinePartAvailabilityDiscrepanciesPerSupplierMap,
    supplierCityMap,
    supplierInventoryDeltaQuantityMap,
  ]);

  return (
    <>
      {pinnedMetrics.length > 0 && (
        <Box
          marginTop={5}
          maxW="100%"
          borderRadius="xl"
          fontFamily="Nunito, sans-serif"
          bg="#F3F4F6"
          paddingTop={4}
          paddingBottom={6}
          paddingLeft={6}
          paddingRight={6}
          boxShadow="md"
        >
          <Flex mb={4} alignItems="center" gap={2}>
            <Text fontSize="lg" fontWeight="bold" color="gray.900">
              📌
            </Text>
            <SupplierText textStyle="header">Pinned Suppliers</SupplierText>
          </Flex>

          <Flex flexWrap="wrap" gap={6}>
            {pinnedMetrics.map(([groupKey, metrics], index) => (
              <Flex
                key={groupKey}
                maxW="32%"
                direction="column"
                flex="1 1 300px"
                bg="white"
                px={6}
                py={5}
                borderRadius="lg"
                boxShadow="sm"
                transition="all 0.25s"
                _hover={{ boxShadow: "md", bg: "gray.50" }}
                cursor="pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  const supplierCity = supplierCityMap.get(metrics.supplierId);
                  handleAddToSelectedSupplierBucket(
                    {
                      _id: metrics.supplierId,
                      supplierName: metrics.supplierName,
                      distanceKm: metrics.distanceKm,
                      supplierCity,
                    },
                    pinnedHitMap.get(groupKey),
                  );
                }}
              >
                <Flex justify="space-between" align="center">
                  <SupplierText textStyle="title" isTruncated>
                    {metrics.supplierName}
                  </SupplierText>

                  <Flex gap={2} align="center">
                    {metrics.isAssociated && (
                      <Badge
                        colorScheme="blue"
                        px={2}
                        py={0.5}
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="bold"
                        textTransform="none"
                      >
                        Associated
                      </Badge>
                    )}
                  </Flex>
                </Flex>

                {/* Distance */}
                <Flex
                  mt={1}
                  align="center"
                  color="gray.500"
                  gap={0.5}
                  fontWeight={700}
                  marginLeft={-0.5}
                >
                  <MdLocationOn
                    fontSize="1rem"
                    color={
                      metrics.distanceKm !== undefined &&
                      metrics.distanceKm !== Infinity
                        ? "#3b82f6"
                        : "#E40133"
                    }
                  />
                  <SupplierText textStyle="subtitle" marginTop={0.4}>
                    {metrics.distanceKm !== undefined &&
                    metrics.distanceKm !== Infinity
                      ? `${metrics.distanceKm.toFixed(1)} km away`
                      : "Unavailable"}
                  </SupplierText>
                </Flex>

                {/* Progress Badges */}
                <Flex mt={3} gap={3} flexWrap="wrap">
                  {metrics.fulfilledPercent !== undefined && (
                    <SupplierProgressBadge
                      type="fulfilled"
                      value={metrics.fulfilledPercent}
                      variant="pinned"
                    />
                  )}
                  <SupplierProgressBadge
                    type="coverage"
                    value={metrics.coverage}
                    totalHits={metrics.totalHits}
                    variant="pinned"
                  />

                  {metrics.totalPartPercentage !== undefined && (
                    <SupplierProgressBadge
                      type="quantity"
                      value={metrics.totalPartPercentage}
                      variant="pinned"
                    />
                  )}
                </Flex>

                {/* Action Buttons */}
                <Flex mt={5} gap={2}>
                  <Button
                    flex="1"
                    size="md"
                    borderRadius="md"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectListItem(
                        {
                          _id: metrics.supplierId,
                          supplierName: metrics.supplierName,
                        },
                        pinnedHitMap.get(groupKey),
                      );
                      onSelectionClose();
                    }}
                    fontWeight="bold"
                    fontSize={{
                      base: "xs",
                      "2xl": "md",
                    }}
                    leftIcon={
                      <Icon as={FaUserCheck} boxSize={3.5} color="#10B981" />
                    }
                    px={3}
                    py={1.5}
                    bg="#f0fdf4"
                    color="#16a34a"
                    _hover={{ bg: "#dcfce7" }}
                  >
                    {isEstimateOrder ? "View Estimate" : "Allocate"}
                  </Button>
                  <RequestPartConfirmationButton
                    supplierId={metrics.supplierId}
                    onClick={() => onRequestPartConfirmation?.(groupKey)}
                  />
                  <Button
                    size="md"
                    borderRadius="md"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSupplierClick(groupKey);
                    }}
                    fontWeight="bold"
                    fontSize="sm"
                    px={3}
                    py={1.5}
                    bg="#eff6ff"
                    color="#4b85f2"
                    _hover={{ bg: "#d2e4fb" }}
                  >
                    <Icon as={BsEyeFill} boxSize={4} color="#2966eb" />
                  </Button>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}
    </>
  );
};

export const GetPinnedComponent = ({
  handleSupplierClick,
  onRequestPartConfirmation,
  pinnedSupplierIds,
  setIsPinnedItem,
  preFacetedPartNumbers,
  typesenseInstantsearchAdapter,
  onSelectionClose,
  customerSupplierAssociations,
  customerGeoPoint,
  handleSelectListItem,
  handleAddToSelectedSupplierBucket,
  setPinnedHitMap,
  supplierDiscoveryCollectionName,
  preFacetedPartNumbersMap,
  totalRequestedQuantity,
  pinnedHitMap,
  selectedSuppliers = [],
  supplierCityMap,
  isEstimateOrder,
  offlinePartAvailabilityDiscrepanciesPerSupplierMap,
  supplierInventoryDeltaQuantityMap,
}: GetPinnedComponentProps) => {
  const generateOptimizedFilter = useMemo(() => {
    return (
      parts: any[],
      supplierIds: string[],
      excludeSupplierIds: string[] = [],
    ) => {
      const partNumbers = parts.map((part) => part.rootPartNumber);
      const filterConditions = [
        `supplierId:=[${supplierIds.join(",")}]`,
        `rootPartNumber:=[${partNumbers.join(",")}]`,
      ];

      if (excludeSupplierIds.length > 0) {
        const exclusionFilter = `supplierId:!=[${excludeSupplierIds.join(
          ",",
        )}]`;
        filterConditions.push(`${exclusionFilter}`);
      }

      return filterConditions.join(" && ");
    };
  }, []);

  const pinnedSupplierFilter = useMemo(() => {
    const selectedSupplierIds = selectedSuppliers.map(
      (supplier) => supplier.supplierId,
    );

    return generateOptimizedFilter(
      preFacetedPartNumbers,
      pinnedSupplierIds,
      selectedSupplierIds,
    );
  }, [
    preFacetedPartNumbers,
    pinnedSupplierIds,
    selectedSuppliers,
    generateOptimizedFilter,
  ]);

  return (
    typesenseInstantsearchAdapter && (
      <InstantSearch
        key={`pinned-search-${preFacetedPartNumbers.length}-${selectedSuppliers.length}`}
        indexName={supplierDiscoveryCollectionName}
        searchClient={typesenseInstantsearchAdapter.searchClient}
      >
        <Configure filters={pinnedSupplierFilter} />
        <PinnedData
          handleSupplierClick={handleSupplierClick}
          onRequestPartConfirmation={onRequestPartConfirmation}
          setIsPinnedItem={setIsPinnedItem}
          preFacetedPartNumbers={preFacetedPartNumbers}
          onSelectionClose={onSelectionClose}
          customerSupplierAssociations={customerSupplierAssociations}
          customerGeoPoint={customerGeoPoint}
          handleSelectListItem={handleSelectListItem}
          handleAddToSelectedSupplierBucket={handleAddToSelectedSupplierBucket}
          setPinnedHitMap={setPinnedHitMap}
          typesenseInstantsearchAdapter={typesenseInstantsearchAdapter}
          supplierDiscoveryCollectionName={supplierDiscoveryCollectionName}
          pinnedSupplierIds={pinnedSupplierIds}
          preFacetedPartNumbersMap={preFacetedPartNumbersMap}
          totalRequestedQuantity={totalRequestedQuantity}
          pinnedHitMap={pinnedHitMap}
          supplierCityMap={supplierCityMap}
          isEstimateOrder={isEstimateOrder}
          offlinePartAvailabilityDiscrepanciesPerSupplierMap={
            offlinePartAvailabilityDiscrepanciesPerSupplierMap
          }
          supplierInventoryDeltaQuantityMap={supplierInventoryDeltaQuantityMap}
        />
      </InstantSearch>
    )
  );
};
